import React, { forwardRef } from "react";
import * as THREE from "three";
import useThreeJsLoader from "../../../zustand/stores/useThreeJsLoader";
import { useShallow } from "zustand/react/shallow";
import { useGLTF, useTexture } from "@react-three/drei";

const moldings = {
  versailles: "versaillesFrame",
  versaillesblanc: "versaillesWhiteFrame",
  default: "designFrame",
};
const types = {
  square: "",
  landscape: "Landscape",
  portrait: "Portrait",
};
const materials = {
  design: "designMoldingTexture",
  oak: "oakMoldingTexture",
  white: "whiteMoldingTexture",
  wood: "woodMoldingTexture",
  versailles: "gold",
  versaillesblanc: "versaillesWhiteMoldingTexture",
};

const MoldingsManager = forwardRef((props, ref) => {
  // Design frames GlTf's & textures import
  const [designFrame, designFrameLandscape, designFramePortrait] = useGLTF([
    "/moldings/designFrame.glb",
    "/moldings/designFrameLandscape.glb",
    "/moldings/designFramePortrait.glb",
  ]);

  const [
    designMoldingTexture,
    oakMoldingTexture,
    whiteMoldingTexture,
    woodMoldingTexture,
    versaillesWhiteMoldingTexture,
    versaillesWhiteMoldingNormalMap,
  ] = useTexture([
    "/moldings/designMoldingTexture.jpg",
    "/moldings/oakMoldingTexture.jpg",
    "/moldings/whiteMoldingTexture.jpg",
    "/moldings/woodMoldingTexture.jpg",
    "/moldings/versaillesWhiteMoldingTexture.png",
    "/moldings/versaillesWhiteMoldingNormalMap.jpg",
  ]);
  versaillesWhiteMoldingTexture.flipY = false;
  versaillesWhiteMoldingNormalMap.flipY = false;

  // Versailles frames GlTf's import
  const [versaillesFrame, versaillesFrameLandscape, versaillesFramePortrait] =
    useGLTF([
      "/moldings/versaillesFrame.glb",
      "/moldings/versaillesFrameLandscape.glb",
      "/moldings/versaillesFramePortrait.glb",
    ]);

  // Versailles white frames GlTf's import
  const [
    versaillesWhiteFrame,
    versaillesWhiteFrameLandscape,
    versaillesWhiteFramePortrait,
  ] = useGLTF([
    "/moldings/versaillesWhiteFrame.glb",
    "/moldings/versaillesWhiteFrameLandscape.glb",
    "/moldings/versaillesWhiteFramePortrait.glb",
  ]);

  const { molding } = useThreeJsLoader(
    useShallow((state) => ({
      molding: state.molding,
    }))
  );

  const geometries = {
    designFrame,
    designFrameLandscape,
    designFramePortrait,
    versaillesFrame,
    versaillesFrameLandscape,
    versaillesFramePortrait,
    versaillesWhiteFrame,
    versaillesWhiteFrameLandscape,
    versaillesWhiteFramePortrait,
  };
  const textures = {
    designMoldingTexture,
    oakMoldingTexture,
    whiteMoldingTexture,
    woodMoldingTexture,
    versaillesWhiteMoldingTexture,
  };
  const finalMolding = props.molding || molding;
  const myGeometry =
    moldings[
      ["versailles", "versaillesblanc"].includes(finalMolding)
        ? finalMolding
        : "default"
    ] + types[!props.type ? "square" : props.type];
  const myMaterial = materials[finalMolding];
  const isNotVersailles = finalMolding !== "versailles";

  if (finalMolding === "none") {
    return null;
  } else {
    if (ref.current) {
      if (props.type === "portrait") ref.current.scale.y = 1 / 1.34;
      else if (props.type === "landscape") ref.current.scale.x = 1 / 1.34;

      if (isNotVersailles) {
        const tmp =
          finalMolding !== "versaillesblanc"
            ? { map: textures[myMaterial], side: THREE.DoubleSide }
            : {
                map: textures[myMaterial],
                side: THREE.DoubleSide,
                normalMap: versaillesWhiteMoldingNormalMap,
              };
        ref.current.material = new THREE.MeshStandardMaterial(tmp);
      } else {
        ref.current.material = geometries[myGeometry].materials[myMaterial];
      }
    }
    return (
      <mesh
        castShadow
        receiveShadow
        geometry={geometries[myGeometry].nodes[myGeometry].geometry}
        position={[0, 0, -0.005]}
        ref={ref}
      />
    );
  }
});

// useGLTF.preload([
//   "/moldings/designFrame.glb",
//   "/moldings/designFrameLandscape.glb",
//   "/moldings/designFramePortrait.glb",
//   "/moldings/versaillesFrame.glb",
//   "/moldings/versaillesFrameLandscape.glb",
//   "/moldings/versaillesFramePortrait.glb",
//   "/moldings/versaillesWhiteFrame.glb",
//   "/moldings/versaillesWhiteFrameLandscape.glb",
//   "/moldings/versaillesWhiteFramePortrait.glb",
// ]);

export default MoldingsManager;
